// OrgPickerSheet — family admin picks which org serves their family.
//
// A family can be linked to zero or many orgs (PRD §6.2 PRO + 049
// simplification). The family admin picks the family's links via
// the `set_family_orgs` RPC, which:
//   - validates each new org is approved and not suspended
//   - replaces the family's link set in `family_org_links` (atomic)
//   - auto-declines any pending `match_requests` for the family
// (The single-org `change_family_org` RPC remains for back-compat.)
//
// RLS `orgs_family_admin_browse` (migration 024) makes every
// approved, non-suspended org visible to family admins so they
// can browse the list.
import { useState } from "react";
import { M3BottomSheet } from "@/components/M3BottomSheet";
import { Button } from "@/components/ui/button";
import { Stagger, StaggerItem } from "@/components/FadeIn";
import { isAuthConfigured, supabase } from "@/lib/supabase";
import { useSupabaseQuery } from "@/lib/dbHooks";

type OrgPickerSheetProps = {
  currentOrgId: string | null;
  currentOrgName: string | null;
  familyId: string | null;
  onClose: () => void;
  onChanged: () => void;
};

type OrgRow = {
  id: string;
  name: string;
  district: string | null;
  description: string | null;
};

export function OrgPickerSheet({
  currentOrgId,
  currentOrgName,
  familyId,
  onClose,
  onChanged,
}: OrgPickerSheetProps) {
  const [picking, setPicking] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Approved, non-suspended orgs. RLS makes this query safe.
  const orgsQ = useSupabaseQuery<OrgRow[]>(async () => {
    if (!isAuthConfigured()) return { data: [], error: null };
    const { data, error } = await supabase
      .from("orgs")
      .select("id, name, district, description")
      .eq("approved", true)
      .eq("suspended", false)
      .order("name", { ascending: true });
    return { data: data ?? [], error };
  }, []);

  const onPick = async (orgId: string): Promise<void> => {
    if (!familyId) {
      setErrMsg("尚未載入家庭資料。");
      return;
    }
    if (orgId === currentOrgId) {
      setErrMsg("已經由呢個機構服務。");
      return;
    }
    if (!window.confirm(
      "確認切換機構？目前待確認嘅配對請求會自動取消，"
      + "新機構嘅管理員會喺收到申請後 24 小時內跟進。",
    )) {
      return;
    }
    setPicking(orgId);
    setErrMsg(null);
    const { error } = await supabase.rpc("change_family_org", {
      p_family_id: familyId,
      p_new_org_id: orgId,
    });
    setPicking(null);
    if (error) {
      setErrMsg("切換失敗：" + error.message);
      return;
    }
    onChanged();
  };

  return (
    <M3BottomSheet onClose={onClose} title="揀選配對機構">
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          目前由 <span className="font-semibold text-foreground">{currentOrgName ?? "FamChest 平台"}</span> 為你嘅家庭服務。
          換機構後，待確認嘅配對請求會自動取消，由新機構接手。
        </p>

        {errMsg && (
          <p className="rounded-xl border border-rose-300/70 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {errMsg}
          </p>
        )}

        <Stagger as="ul" className="space-y-2" gap={0.05}>
          {orgsQ.loading ? (
            <li className="rounded-2xl border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
              載入機構名單…
            </li>
          ) : (orgsQ.data ?? []).length === 0 ? (
            <li className="rounded-2xl border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
              暫時冇已認證嘅機構。
            </li>
          ) : (
            (orgsQ.data ?? []).map((o) => {
              const isCurrent = o.id === currentOrgId;
              return (
                <StaggerItem key={o.id} as="li">
                  <div
                    className={
                      "rounded-2xl border p-3 " +
                      (isCurrent
                        ? "border-primary/40 bg-primary/5"
                        : "border-border/40 bg-card")
                    }
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-semibold">{o.name}</span>
                          {o.district && (
                            <span className="text-[11px] text-muted-foreground">
                              · {o.district}
                            </span>
                          )}
                          {isCurrent && (
                            <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                              現時
                            </span>
                          )}
                        </div>
                        {o.description && (
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {o.description}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant={isCurrent ? "ghost" : "default"}
                        disabled={isCurrent || picking !== null}
                        onClick={() => { void onPick(o.id); }}
                      >
                        {picking === o.id ? "處理中…" : isCurrent ? "使用中" : "選擇"}
                      </Button>
                    </div>
                  </div>
                </StaggerItem>
              );
            })
          )}
        </Stagger>
      </div>
    </M3BottomSheet>
  );
}
